import { createSelector } from 'reselect';

import { selectEntitiesAchievements } from '../../../selectors/entities/achievements/entitiesAchievementsPrimary.selector';
import { selectEntitiesMissionsEntityAchievementIds } from '../../../selectors/entities/missions/entitiesMissionsPrimary.selector';
import { type Selector } from '../../../types/selectors';
import { createResultEqualSelector } from '../../../utils/reselect';
import { type AchievementReward } from '../types/state/achievements';
import { type AchievementState } from '../types/state/achievements';
import { MissionRewardTypes } from '../types/state/missions';

import { selectAchievements } from './achievements';
import { selectMissionIds } from './missions';
import { getAchievementIdFromProps } from './utils';

export const selectMissionAchievements: Selector<Array<AchievementState>> = createResultEqualSelector(
  [selectEntitiesMissionsEntityAchievementIds, selectEntitiesAchievements],
  (achievementIds, achievements) => achievementIds.map((achievementId) => achievements[achievementId]).filter((k) => k),
);

export const selectMissionAchievement: Selector<AchievementState | undefined> = createSelector(
  [selectMissionAchievements, getAchievementIdFromProps],
  (achievements, achievementId) => achievements.find((achievement) => achievement.achievementId === achievementId),
);

export const selectFirstMissionAchievement: Selector<AchievementState | undefined> = createSelector(
  [selectMissionAchievements],
  (achievements) => achievements[0],
);

export const selectMissionAchievementDescription: Selector<string | undefined> = createSelector(
  [selectMissionAchievement],
  (achievement) => achievement?.description,
);

export const selectMissionAchievementMinBetOdds: Selector<number | undefined> = createSelector(
  [selectMissionAchievement],
  (achievement) => achievement?.eligibility?.minBetOdds,
);

export const selectMissionAchievementMinPlacedAmount: Selector<number | null | undefined> = createSelector(
  [selectMissionAchievement],
  (achievement) => achievement?.eligibility?.minPlacedAmount,
);

export const selectMissionAchievementReward: Selector<AchievementReward | null | undefined> = createSelector(
  [selectMissionAchievement],
  (achievement) => achievement?.reward,
);

export const selectMissionAchievementsRewards: Selector<Array<AchievementReward | null | undefined>> =
  createResultEqualSelector([selectMissionAchievements], (achievements) =>
    achievements.map((achievement) => achievement.reward).filter((k) => k),
  );

export const selectMissionCumulatedFreebetsRewardsFromAchievements: Selector<number> = createSelector(
  selectMissionAchievementsRewards,
  (rewards) =>
    rewards.reduce(
      (result, reward) =>
        reward?.rewardType === MissionRewardTypes.FREEBETS && reward.rewardParams?.amount
          ? result + reward.rewardParams.amount
          : result,
      0,
    ),
);

export const selectAchievementsByMission: Selector<{ [index: string]: Array<AchievementState> }> = createSelector(
  [selectMissionIds, selectAchievements],
  (missionIds, achievements) => {
    const result: Record<string, Array<AchievementState>> = {};

    for (const missionId of missionIds) {
      result[missionId] = [
        ...(result[missionId] ?? []),
        ...achievements.filter((achievement) => achievement.missionId === missionId),
      ];
    }

    return result;
  },
);
